import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { useAuth } from './index';
import { buildApiUrl } from '../config/api';

type RealtimeEchoModule = typeof import('../util/realtimeEcho');

function loadRealtimeEcho(): Promise<RealtimeEchoModule> {
    return import('../util/realtimeEcho');
}

interface NotificationContextType {
    unreadCount: number;
    loading: boolean;
    fetchUnreadCount: () => Promise<void>;
    updateUnreadCount: (count: number) => void;
    decrementUnreadCount: () => void;
    incrementUnreadCount: () => void;
    incrementUnreadCountBy: (count: number) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

interface NotificationProviderProps {
    children: ReactNode;
}

// Minimum time between API calls (in ms)
const DEBOUNCE_DELAY = 2000;
// Polling interval when app is visible (in ms) - increased from 30s to 60s since we have realtime updates
const POLLING_INTERVAL = 60000;

export function NotificationProvider({ children }: NotificationProviderProps) {
    const [unreadCount, setUnreadCount] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(false);
    const auth = useAuth();
    
    // Track last fetch time and pending timeout for debouncing
    const lastFetchTimeRef = useRef<number>(0);
    const pendingFetchRef = useRef<NodeJS.Timeout | null>(null);

    const fetchUnreadCount = useCallback(async () => {
        if (!auth.token) return;
        
        try {
            setLoading(true);
            const response = await fetch(buildApiUrl('/api/notifications/unread-count'), {
                headers: {
                    'Authorization': `Bearer ${auth.token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                const count = data.unread_count || 0;
                setUnreadCount(count);
                
                // Update iOS badge count
                if (typeof window !== 'undefined' && (window as any).Capacitor) {
                    const { Capacitor } = await import('@capacitor/core');
                    if (Capacitor.getPlatform() === 'ios') {
                        try {
                            const { Badge } = await import('@capawesome/capacitor-badge');
                            await Badge.set({ count });
                        } catch (error) {
                            // Badge plugin might not be available, ignore
                            console.debug('Could not update badge count:', error);
                        }
                    }
                }
            }
        } catch (error) {
            console.error('Error fetching unread count:', error);
        } finally {
            setLoading(false);
        }
    }, [auth.token]);

    const updateUnreadCount = useCallback((newCount: number) => {
        setUnreadCount(newCount);
    }, []);

    const decrementUnreadCount = useCallback(() => {
        setUnreadCount(prev => Math.max(0, prev - 1));
    }, []);

    const incrementUnreadCount = useCallback(() => {
        setUnreadCount(prev => prev + 1);
    }, []);

    const incrementUnreadCountBy = useCallback((count: number) => {
        setUnreadCount(prev => prev + count);
    }, []);

    useEffect(() => {
        void loadRealtimeEcho().then((m) => m.registerCapacitorRealtimeLifecycle());
    }, []);

    useEffect(() => {
        if (!auth.token) {
            void loadRealtimeEcho().then((m) => m.disconnectEchoSingleton());
        }
    }, [auth.token]);

    // Debounced version of fetchUnreadCount to prevent rapid consecutive calls
    const debouncedFetchUnreadCount = useCallback(() => {
        const now = Date.now();
        const timeSinceLastFetch = now - lastFetchTimeRef.current;
        
        // Clear any pending fetch
        if (pendingFetchRef.current) {
            clearTimeout(pendingFetchRef.current);
            pendingFetchRef.current = null;
        }
        
        // If enough time has passed, fetch immediately
        if (timeSinceLastFetch >= DEBOUNCE_DELAY) {
            lastFetchTimeRef.current = now;
            fetchUnreadCount();
        } else {
            // Otherwise, schedule a fetch after the remaining debounce time
            const delay = DEBOUNCE_DELAY - timeSinceLastFetch;
            pendingFetchRef.current = setTimeout(() => {
                lastFetchTimeRef.current = Date.now();
                fetchUnreadCount();
                pendingFetchRef.current = null;
            }, delay);
        }
    }, [fetchUnreadCount]);

    useEffect(() => {
        fetchUnreadCount();

        if (!auth.token || !auth.user) {
            return;
        }

        let cancelled = false;
        let teardown: (() => void) | undefined;

        void loadRealtimeEcho().then(({ getOrCreateEcho }) => {
            if (cancelled) {
                return;
            }

            const echo = getOrCreateEcho(auth.token!);
            const channelName = `notifications.user.${auth.user!.id}`;
            const channel = echo.private(channelName);

            const handler = (payload: unknown) => {
                setUnreadCount(prev => prev + 1);
                try {
                    window.dispatchEvent(new CustomEvent('notification:new', { detail: payload }));
                } catch (error) {
                    console.error('NotificationContext: Error dispatching event:', error);
                }
            };

            channel.listen('.notification.created', handler);

            teardown = () => {
                try {
                    channel.stopListening('.notification.created');
                    echo.leave(channelName);
                } catch {
                    // ignore teardown errors
                }
            };
        });

        return () => {
            cancelled = true;
            teardown?.();
        };
    }, [auth.token, auth.user, fetchUnreadCount]);

    // Update iOS badge count whenever unreadCount changes
    useEffect(() => {
        const updateBadge = async () => {
            if (typeof window !== 'undefined' && (window as any).Capacitor) {
                try {
                    const { Capacitor } = await import('@capacitor/core');
                    if (Capacitor.getPlatform() === 'ios') {
                        const { Badge } = await import('@capawesome/capacitor-badge');
                        await Badge.set({ count: unreadCount });
                    }
                } catch (error) {
                    // Badge plugin might not be available, ignore silently
                    console.debug('Could not update badge count:', error);
                }
            }
        };
        
        updateBadge();
    }, [unreadCount]);

    // Listen for user logout to clear notifications and badge
    useEffect(() => {
        const handleUserLogout = async () => {
            void loadRealtimeEcho().then((m) => m.disconnectEchoSingleton());
            // Reset unread count
            setUnreadCount(0);
            
            // Clear badge count (iOS)
            if (typeof window !== 'undefined' && (window as any).Capacitor) {
                try {
                    const { Capacitor } = await import('@capacitor/core');
                    if (Capacitor.getPlatform() === 'ios') {
                        const { Badge } = await import('@capawesome/capacitor-badge');
                        await Badge.clear();
                    }
                } catch (error) {
                    console.debug('Could not clear badge on logout:', error);
                }
            }
        };

        window.addEventListener('user-logged-out', handleUserLogout);
        
        return () => {
            window.removeEventListener('user-logged-out', handleUserLogout);
        };
    }, []);

    // Listen for push notifications on mobile and app state changes
    useEffect(() => {
        if (!auth.token || !auth.user) {
            // User logged out - clear count and badge
            setUnreadCount(0);
            return;
        }

        // Handle push notification received (when app is in foreground)
        const handlePushNotificationReceived = () => {
            // Increment count when push notification is received
            incrementUnreadCount();
            // Also refresh from server to ensure accuracy (debounced)
            debouncedFetchUnreadCount();
        };

        // Handle push notification action (when user taps notification)
        const handlePushNotificationAction = () => {
            // Refresh count when user taps on notification (debounced to prevent duplicates)
            debouncedFetchUnreadCount();
        };

        // Listen for push notification events
        window.addEventListener('push-notification-received', handlePushNotificationReceived as EventListener);
        window.addEventListener('push-notification-action', handlePushNotificationAction as EventListener);

        // Listen for app state changes (when app comes to foreground)
        // Only use visibilitychange as it's more reliable and avoids duplicate calls from focus events
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                // Refresh count when app comes to foreground (debounced)
                debouncedFetchUnreadCount();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        // Set up a periodic refresh as a fallback (every 60 seconds when app is visible)
        // Increased from 30s since we have realtime WebSocket updates
        const intervalId = setInterval(() => {
            if (document.visibilityState === 'visible' && auth.token) {
                fetchUnreadCount();
            }
        }, POLLING_INTERVAL);

        return () => {
            window.removeEventListener('push-notification-received', handlePushNotificationReceived as EventListener);
            window.removeEventListener('push-notification-action', handlePushNotificationAction as EventListener);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            clearInterval(intervalId);
            // Clear any pending debounced fetch
            if (pendingFetchRef.current) {
                clearTimeout(pendingFetchRef.current);
            }
        };
    }, [auth.token, auth.user, fetchUnreadCount, debouncedFetchUnreadCount, incrementUnreadCount]);

    const value: NotificationContextType = {
        unreadCount,
        loading,
        fetchUnreadCount,
        updateUnreadCount,
        decrementUnreadCount,
        incrementUnreadCount,
        incrementUnreadCountBy
    };

    return (
        <NotificationContext.Provider value={value}>
            {children}
        </NotificationContext.Provider>
    );
}

export function useNotifications(): NotificationContextType {
    const context = useContext(NotificationContext);
    if (context === undefined) {
        throw new Error('useNotifications must be used within a NotificationProvider');
    }
    return context;
} 